import classNames from "classnames"
import { centsToDollars } from "legacy/helpers.es6"
import _ from "lodash"
import moment from "moment"
import PropTypes from "prop-types"
import React from "react"

import Modal from "src/components/Modal"

class PreservedRateModal extends React.Component {
  constructor(props) {
    super(props)

    // Bind methods
    this.renderModifiers = this.renderModifiers.bind(this)
    this.renderFooterNote = this.renderFooterNote.bind(this)
    this.anyPersistedPreservedRates = this.anyPersistedPreservedRates.bind(this)
    this.renderOccupancyNote = this.renderOccupancyNote.bind(this)
    this.renderMinNightsNote = this.renderMinNightsNote.bind(this)
    this.renderTelescopeCta = this.renderTelescopeCta.bind(this)
    this.renderModalContent = this.renderModalContent.bind(this)
  }

  renderModifiers(modifiers) {
    if (modifiers.length === 0) return null

    return (
      <div>
        {modifiers.map((modifier, index) => {
          return (
            <div key={index}>{`${modifier.label}: ${modifier.description} ${
              modifier.price !== undefined
                ? `(${centsToDollars(modifier.price)})`
                : ""
            }`}</div>
          )
        })}
      </div>
    )
  }

  renderFooterNote() {
    if (this.renderOccupancyNote() || this.renderMinNightsNote()) {
      return (
        <tr>
          <td colSpan="100">
            {this.renderOccupancyNote()}
            {this.renderMinNightsNote()}
          </td>
        </tr>
      )
    }
    return null
  }

  anyPersistedPreservedRates() {
    return this.props.preservedRates.some(
      (preservedRate) => preservedRate.created_at !== null
    )
  }

  renderOccupancyNote() {
    if (
      _.filter(
        this.props.preservedRates,
        (preservedRate) => preservedRate.rate !== preservedRate.base_rate
      ).length
    ) {
      return <p>* Indicates a higher rate based on higher occupancy.</p>
    }
    return null
  }

  renderMinNightsNote() {
    if (
      _.filter(
        _.flatten(
          _.map(this.props.preservedRates, (preservedRate) => {
            return preservedRate.modifiers
          })
        ),
        (modifier) => !modifier.price_modifier
      ).length
    ) {
      return <p>Note: Totals exclude Minimum Nights modifier calculations.</p>
    }
    return null
  }

  renderTelescopeCta() {
    return (
      <a
        href={$context.telescopeCtaPath || this.props.telescopeCtaPath}
        target="_blank"
        rel="noreferrer"
      >
        <i
          className="fa-kit fa-info text-gray-500"
          data-toggle="tooltip"
          data-placement="top"
          title="Occupany-based dynamic rates are a premium feature of Dockwa Telescope. Inquire to learn more."
        />
      </a>
    )
  }

  renderModalContent() {
    if (this.props.loading) {
      return (
        <div className="flex w-full items-center justify-center">
          <i className="fa-solid fa-spinner-third fa-spin text-2xl text-gray-600" />
        </div>
      )
    } else {
      return (
        <table className="table-default table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Base Rate</th>
              <th>
                Dynamic Rate{" "}
                {!($context.telescopeEnabled || this.props.telescopeEnabled) &&
                  this.renderTelescopeCta()}
              </th>
              <th>Time frame</th>
              {($context.telescopeEnabled || this.props.telescopeEnabled) && (
                <th>Occupancy</th>
              )}
              <th>Subtotal</th>
              <th>Modifiers</th>
              <th>Estimated Total</th>
              {this.anyPersistedPreservedRates() && <th>Preserved On</th>}
            </tr>
          </thead>
          <tbody>
            {this.props.preservedRates.map((preservedRate) => {
              return (
                <tr key={preservedRate.night}>
                  <td>{moment(preservedRate.night).format("ddd, MMM Do")}</td>
                  <td>
                    {preservedRate.rate_type === "by_length"
                      ? `${centsToDollars(preservedRate.base_rate)}/ft`
                      : centsToDollars(preservedRate.base_rate)}
                  </td>
                  <td
                    className={classNames({
                      "font-semibold text-teal-700":
                        preservedRate.base_rate !== preservedRate.rate,
                    })}
                  >
                    {preservedRate.rate_type === "by_length"
                      ? `${centsToDollars(preservedRate.rate)}/ft`
                      : centsToDollars(preservedRate.rate)}
                    {preservedRate.base_rate !== preservedRate.rate && "*"}
                  </td>
                  <td>{preservedRate.timeframe}</td>
                  {($context.telescopeEnabled ||
                    this.props.telescopeEnabled) && (
                    <td>
                      {(parseFloat(preservedRate.occupancy) * 100).toFixed(1)}%
                    </td>
                  )}
                  <td>{centsToDollars(preservedRate.subtotal)}</td>
                  <td>{this.renderModifiers(preservedRate.modifiers)}</td>
                  <td>{centsToDollars(preservedRate.total)}</td>
                  {this.anyPersistedPreservedRates() && (
                    <td>
                      {preservedRate.created_at
                        ? moment(preservedRate.created_at).format(
                            "MMM Do, h:mm a"
                          )
                        : ""}
                    </td>
                  )}
                </tr>
              )
            })}
            {this.renderFooterNote()}
          </tbody>
        </table>
      )
    }
  }

  render() {
    return (
      <Modal
        isOpen={true}
        onClose={this.props.handleModalClose}
        size="extraLarge"
        maxWidth="120rem"
      >
        <Modal.Header title="Nightly Rates" />
        <Modal.Body>{this.renderModalContent()}</Modal.Body>
        <Modal.Footer
          onlyCancel={true}
          cancelBtnText="Cancel"
          onClose={this.props.handleModalClose}
        />
      </Modal>
    )
  }
}

// Note: $context is loaded globally, not via an import

PreservedRateModal.propTypes = {
  preservedRates: PropTypes.array.isRequired,
  handleModalClose: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  telescopeCtaPath: PropTypes.string,
  telescopeEnabled: PropTypes.bool,
}

export default PreservedRateModal
